"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "@/lib/api";
import { Spinner, XCircle } from "@/lib/icons";
import CustomSelect from "@/components/custom-select";
import * as s from "@/lib/styles";
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickSeries,
  HistogramSeries,
  HistogramData,
  LineSeries,
  LineData,
  Time,
  LineStyle,
} from "lightweight-charts";

interface PriceRow {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface SpreadChartOverlay {
  side: "put" | "call";
  shortStrike: number;
  longStrike: number;
  entryTime?: string | null;
  exitTime?: string | null;
  expirationDate?: string | null;
}

// Build a chart overlay from a spread row. Every caller (scan results, open
// spreads, open orders, closed history — on both the VS and 0DTE pages) shares
// this shape; the only real variation is which of entry/exit/expiration is
// known. Centralizing it keeps the put/call mapping and field names in one
// place so a chart never silently loses (e.g.) its expiration marker.
export function makeSpreadOverlay(o: {
  isPut: boolean;
  shortStrike: number;
  longStrike: number;
  entryTime?: string | null;
  exitTime?: string | null;
  expirationDate?: string | null;
}): SpreadChartOverlay {
  return {
    side: o.isPut ? "put" : "call",
    shortStrike: o.shortStrike,
    longStrike: o.longStrike,
    entryTime: o.entryTime ?? null,
    exitTime: o.exitTime ?? null,
    expirationDate: o.expirationDate ?? null,
  };
}

// --- Indicator helpers ---

function computeSMA(closes: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (i < period - 1) { out.push(null); continue; }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += closes[j];
    out.push(sum / period);
  }
  return out;
}

function computeEMA(closes: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [];
  const k = 2 / (period + 1);
  let ema: number | null = null;
  for (let i = 0; i < closes.length; i++) {
    if (i < period - 1) { out.push(null); continue; }
    if (ema === null) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += closes[j];
      ema = sum / period;
    } else {
      ema = closes[i] * k + ema * (1 - k);
    }
    out.push(ema);
  }
  return out;
}

function computeBollinger(closes: number[], period: number, mult: number) {
  const mid = computeSMA(closes, period);
  const upper: (number | null)[] = [];
  const lower: (number | null)[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (mid[i] === null) { upper.push(null); lower.push(null); continue; }
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) variance += (closes[j] - mid[i]!) ** 2;
    const std = Math.sqrt(variance / period);
    upper.push(mid[i]! + mult * std);
    lower.push(mid[i]! - mult * std);
  }
  return { upper, mid, lower };
}

function computeRSI(closes: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [null];
  const gains: number[] = [];
  const losses: number[] = [];
  for (let i = 1; i < closes.length; i++) {
    const delta = closes[i] - closes[i - 1];
    gains.push(delta > 0 ? delta : 0);
    losses.push(delta < 0 ? -delta : 0);
    if (i < period) { out.push(null); continue; }
    if (i === period) {
      const avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
      const avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;
      out.push(avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss));
    } else {
      const prevRsi = out[i - 1];
      if (prevRsi === null) { out.push(null); continue; }
      const prevAvgGain = gains.slice(i - period, i - 1).reduce((a, b) => a + b, 0) / period;
      const prevAvgLoss = losses.slice(i - period, i - 1).reduce((a, b) => a + b, 0) / period;
      const avgGain = (prevAvgGain * (period - 1) + gains[i - 1]) / period;
      const avgLoss = (prevAvgLoss * (period - 1) + losses[i - 1]) / period;
      out.push(avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss));
    }
  }
  return out;
}

function computeMACD(closes: number[], fast: number, slow: number, signal: number) {
  const emaFast = computeEMA(closes, fast);
  const emaSlow = computeEMA(closes, slow);
  const macdLine: (number | null)[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (emaFast[i] !== null && emaSlow[i] !== null) macdLine.push(emaFast[i]! - emaSlow[i]!);
    else macdLine.push(null);
  }
  const nonNull = macdLine.filter((v) => v !== null) as number[];
  const sigEma = computeEMA(nonNull, signal);
  const signalLine: (number | null)[] = [];
  const hist: (number | null)[] = [];
  let si = 0;
  for (let i = 0; i < closes.length; i++) {
    if (macdLine[i] === null) { signalLine.push(null); hist.push(null); continue; }
    const sv = sigEma[si] ?? null;
    signalLine.push(sv);
    hist.push(sv !== null ? macdLine[i]! - sv : null);
    si++;
  }
  return { macd: macdLine, signal: signalLine, histogram: hist };
}

function computeStochastic(highs: number[], lows: number[], closes: number[], kPeriod: number, dPeriod: number) {
  const kLine: (number | null)[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (i < kPeriod - 1) { kLine.push(null); continue; }
    let hh = -Infinity, ll = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (highs[j] > hh) hh = highs[j];
      if (lows[j] < ll) ll = lows[j];
    }
    kLine.push(hh === ll ? 50 : ((closes[i] - ll) / (hh - ll)) * 100);
  }
  const dLine = computeSMA(kLine.map((v) => v ?? 0), dPeriod).map((v, i) => kLine[i] === null ? null : v);
  return { k: kLine, d: dLine };
}

function computeMA(closes: number[], key: string): (number | null)[] {
  const m = key.match(/^(sma|ema)(\d+)$/);
  if (!m) return [];
  const period = parseInt(m[2]);
  return m[1] === "sma" ? computeSMA(closes, period) : computeEMA(closes, period);
}

const MA_OPTIONS = [
  { label: "SMA 10", value: "sma10" },
  { label: "SMA 20", value: "sma20" },
  { label: "SMA 50", value: "sma50" },
  { label: "SMA 100", value: "sma100" },
  { label: "SMA 200", value: "sma200" },
  { label: "EMA 10", value: "ema10" },
  { label: "EMA 20", value: "ema20" },
  { label: "EMA 50", value: "ema50" },
  { label: "EMA 100", value: "ema100" },
  { label: "EMA 200", value: "ema200" },
];

const OVERLAY_OPTIONS = [
  { label: "Bollinger (20,2)", value: "bb20" },
  { label: "Bollinger (20,3)", value: "bb20_3" },
  { label: "Bollinger (50,2)", value: "bb50" },
];

const OSCILLATOR_OPTIONS = [
  { label: "RSI (14)", value: "rsi14" },
  { label: "RSI (9)", value: "rsi9" },
  { label: "RSI (25)", value: "rsi25" },
  { label: "MACD (12,26,9)", value: "macd" },
  { label: "MACD (8,17,9)", value: "macd_fast" },
  { label: "Stochastic (14,3)", value: "stoch14" },
  { label: "Stochastic (5,3)", value: "stoch5" },
];

// --- Component ---

interface PriceChartProps {
  symbol: string;
  spreadOverlay?: SpreadChartOverlay | null;
  // Focus mode: center the daily chart on a specific historical date instead of
  // the trailing now-anchored window. `focusDate` (YYYY-MM-DD) is marked with a
  // vertical line and the visible range is ±3 months around it; `focusLevel`
  // draws a dashed horizontal price line. Used by the Threshold diagnostic.
  focusDate?: string | null;
  focusLevel?: number | null;
  onClose: () => void;
  className?: string;
  // Initial timeframe when the chart opens. Defaults to "1D" (daily history);
  // pass "5m" to open on today's 5-minute intraday session instead. Ignored in
  // focus mode (a historical focusDate always opens on the daily chart).
  defaultTimeframe?: "1D" | "5m";
  // Account hash picks the 5-minute broker (tastytrade DxLink vs Schwab
  // pricehistory). Falls back to `?account=` on the current URL when omitted.
  accountHash?: string | null;
}

function intradayAccountHash(accountHash?: string | null): string {
  if (accountHash) return accountHash;
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("account") ?? "";
}

export default function PriceChart({ symbol, spreadOverlay, focusDate, focusLevel, onClose, className, defaultTimeframe, accountHash }: PriceChartProps) {
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [ma1, setMA1] = useState("");
  const [ma2, setMA2] = useState("");
  const [overlay, setOverlay] = useState("");
  const [oscillator, setOscillator] = useState("");
  const [marketOpen, setMarketOpen] = useState(false);
  // "1D" = daily history (default). "5m" = today's 5-minute intraday session.
  // A historical focusDate always opens on the daily chart; otherwise honor the
  // caller's requested default (e.g. the 0DTE page opens charts on 5m).
  const [timeframe, setTimeframe] = useState<"1D" | "5m">(focusDate ? "1D" : (defaultTimeframe ?? "1D"));
  // OHLC of the bar under the crosshair, shown in a line above the chart.
  // Falls back to the latest bar when the cursor isn't over the chart.
  const [ohlc, setOhlc] = useState<{ d: string; o: number; h: number; l: number; c: number; chg: number | null; v: number | null } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  // Refs to the live-updatable series so the poll below can push the latest
  // quote into the last bar via series.update() — no full chart rebuild, so
  // the user's zoom/pan is preserved.
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);

  const drawSpreadShading = useCallback((
    ctx: CanvasRenderingContext2D,
    priceSeries: ISeriesApi<"Candlestick">,
    shadeLeft: number,
    plotRight: number,
    h: number,
  ) => {
    if (!spreadOverlay) return;
    const short = spreadOverlay.shortStrike;
    const long = spreadOverlay.longStrike;
    if (!(short > 0) || !(long > 0)) return;
    const lower = Math.min(short, long);
    const upper = Math.max(short, long);
    const yUpper = priceSeries.priceToCoordinate(upper);
    const yLower = priceSeries.priceToCoordinate(lower);
    if (yUpper == null || yLower == null) return;
    const top = Math.max(0, Math.min(yUpper, yLower));
    const bottom = Math.min(h, Math.max(yUpper, yLower));
    const width = Math.max(0, plotRight - shadeLeft);
    if (width <= 0) return;
    const fillBand = (y: number, height: number, color: string) => {
      if (height <= 0) return;
      ctx.fillStyle = color;
      ctx.fillRect(shadeLeft, y, width, height);
    };
    const green = "rgba(34, 197, 94, 0.12)";
    const red = "rgba(244, 63, 94, 0.12)";
    const gray = "rgba(148, 163, 184, 0.10)";
    if (spreadOverlay.side === "put") {
      fillBand(0, top, green);
      fillBand(top, bottom - top, gray);
      fillBand(bottom, h - bottom, red);
    } else {
      fillBand(0, top, red);
      fillBand(top, bottom - top, gray);
      fillBand(bottom, h - bottom, green);
    }
  }, [spreadOverlay]);

  useEffect(() => {
    if (timeframe !== "1D") return;
    let cancelled = false;
    setLoading(true);

    // Focus mode: fetch a window centered on `focusDate` — 12 months before (so
    // indicators have lookback across the visible range) through 3 months after.
    // Otherwise fetch the trailing 18 months (displayed range is the last 6
    // months; the extra 12 are indicator lookback). The market-status/live-quote
    // path is skipped in focus mode — appending today's bar to a historical
    // window would land it far outside the range.
    let url: string;
    if (focusDate) {
      const f = new Date(focusDate + "T00:00:00Z");
      const sd = new Date(f); sd.setUTCMonth(sd.getUTCMonth() - 12);
      const ed = new Date(f); ed.setUTCMonth(ed.getUTCMonth() + 3);
      url = `/prices/${encodeURIComponent(symbol)}?start=${sd.toISOString().slice(0, 10)}&end=${ed.toISOString().slice(0, 10)}&limit=5000`;
    } else {
      const fetchStart = new Date();
      fetchStart.setMonth(fetchStart.getMonth() - 18);
      url = `/prices/${encodeURIComponent(symbol)}?start=${fetchStart.toISOString().slice(0, 10)}&limit=5000`;
    }

    Promise.all([
      apiGet<{ rows: PriceRow[] }>(url),
      focusDate ? Promise.resolve(null) : apiGet<{ is_market_open: boolean }>(`/prices/market-status`).catch(() => null),
    ])
      .then(async ([priceResp, marketResp]) => {
        if (cancelled) return;
        if (!cancelled) setMarketOpen(!!marketResp?.is_market_open);
        let sorted = priceResp.rows.sort((a, b) => a.date.localeCompare(b.date));

        if (marketResp?.is_market_open) {
          try {
            const quote = await apiGet<PriceRow>(`/prices/${encodeURIComponent(symbol)}/quote`);
            if (!cancelled && quote.open > 0) {
              const lastDate = sorted.length > 0 ? sorted[sorted.length - 1].date : "";
              if (lastDate === quote.date) {
                sorted[sorted.length - 1] = quote;
              } else {
                sorted = [...sorted, quote];
              }
            }
          } catch { /* quote unavailable, show historical only */ }
        }

        if (!cancelled) setRows(sorted);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, timeframe, focusDate]);

  useEffect(() => {
    if (timeframe !== "1D" || rows.length === 0 || !containerRef.current) return;
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
    const el = containerRef.current;
    const w = el.clientWidth || el.getBoundingClientRect().width || 600;
    const h = el.clientHeight || el.getBoundingClientRect().height || 300;
    const chart = createChart(el, {
      width: w,
      height: h,
      layout: { background: { color: "transparent" }, textColor: "#a0a0b0" },
      // Vertical gridlines come from a Monday-aligned overlay canvas (see
      // drawMondayLines below) — disable the chart's auto vertical lines so
      // we don't end up with two competing sets.
      grid: { vertLines: { visible: false }, horzLines: { color: "#2a2a3e" } },
      // Hide the crosshair's time label on the axis — the date is shown on the
      // OHLC line above the chart instead.
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      // Blank the built-in tick labels — the overlay canvas paints custom
      // bi-weekly Monday labels (day-of-month, with month/year on the first
      // visible Monday). The time-scale strip stays visible so the layout
      // reserves room for our labels at the bottom of the chart.
      timeScale: { borderColor: "#2a2a3e", tickMarkFormatter: () => "" },
      // Logarithmic y-axis: price moves are perceived proportionally, so a
      // 5% move looks the same height whether the stock is at $20 or $200.
      rightPriceScale: { borderColor: "#2a2a3e", mode: 1 /* PriceScaleMode.Logarithmic */ },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderUpColor: "#22c55e",
      borderDownColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
      priceLineVisible: false,
    });
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = null;
    // Build the full set of weekdays in the data's date range. Days that
    // exist in `rows` carry real OHLCV; weekday gaps (market holidays) get
    // a whitespace data point — lightweight-charts still reserves the time
    // slot, giving the chart consistent horizontal density (a market holiday
    // is a visible gap instead of being silently collapsed).
    const holidayWhitespace: { time: Time }[] = (() => {
      if (rows.length === 0) return [];
      const have = new Set(rows.map((r) => r.date));
      const out: { time: Time }[] = [];
      const parse = (s: string) => {
        const [y, m, d] = s.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d));
      };
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      const lastDate = rows[rows.length - 1].date;
      const expDate = spreadOverlay?.expirationDate;
      const end = parse(expDate && expDate > lastDate ? expDate : lastDate);
      for (let d = parse(rows[0].date); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const dow = d.getUTCDay();
        if (dow === 0 || dow === 6) continue; // skip weekends — chart collapses those anyway
        const ds = fmt(d);
        if (!have.has(ds)) out.push({ time: ds as Time });
      }
      return out;
    })();

    const candleData = [
      ...rows.map((r) => ({
        time: r.date as Time,
        open: r.open, high: r.high, low: r.low, close: r.close,
      })),
      ...holidayWhitespace,
    ].sort((a, b) => (a.time as string).localeCompare(b.time as string));
    candleSeries.setData(candleData);

    // Focus mode: dashed horizontal line at the threshold price being studied.
    if (focusLevel && focusLevel > 0) {
      candleSeries.createPriceLine({
        price: focusLevel,
        color: "#06b6d4",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `$${focusLevel.toLocaleString()}`,
      });
    }

    if (spreadOverlay && candleData.length > 1) {
      const startDate = spreadOverlay.entryTime?.slice(0, 10);
      const endDate = spreadOverlay.exitTime?.slice(0, 10);
      const startIndex = startDate
        ? candleData.findIndex((d) => (d.time as string) >= startDate)
        : 0;
      const foundEndIndex = endDate
        ? candleData.findIndex((d) => (d.time as string) >= endDate)
        : -1;
      const endIndex = foundEndIndex >= 0 ? foundEndIndex : candleData.length - 1;
      const strikeLineData = (strike: number) =>
        startIndex < 0 || startIndex > endIndex
          ? []
          : candleData.map((d, i) => (
              i >= startIndex && i <= endIndex
                ? { time: d.time, value: strike }
                : { time: d.time }
            ));
      const shortStrike = chart.addSeries(LineSeries, {
        color: "rgba(250, 204, 21, 0.55)",
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        priceScaleId: "right",
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        title: "",
      });
      shortStrike.setData(strikeLineData(spreadOverlay.shortStrike));
      const longStrike = chart.addSeries(LineSeries, {
        color: "rgba(250, 204, 21, 0.55)",
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        priceScaleId: "right",
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        title: "",
      });
      longStrike.setData(strikeLineData(spreadOverlay.longStrike));
    }

    // Indices (e.g. $RUT, $XSP) have no volume data — price_history stores
    // zeros for them. If the first five rows are all zero-volume, treat the
    // series as volume-less and skip the volume pane (and its SMA overlay)
    // entirely so the chart doesn't waste vertical space on an empty panel.
    const hasVolume = rows.slice(0, 5).some((r) => r.volume > 0);
    if (hasVolume) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: "#4a90d9",
        priceFormat: { type: "volume" as const },
        priceScaleId: "volume",
      }, 1);
      volumeSeriesRef.current = volumeSeries;
      volumeSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.1, bottom: 0 },
      });
      const volumeData = [
        ...rows.map((r) => ({
          time: r.date as Time,
          value: r.volume,
          color: r.close >= r.open ? "#22c55e40" : "#ef444440",
        })),
        ...holidayWhitespace,
      ].sort((a, b) => (a.time as string).localeCompare(b.time as string));
      volumeSeries.setData(volumeData as HistogramData<Time>[]);

      // Volume 50-day SMA
      const volumes = rows.map((r) => r.volume);
      const volSma = computeSMA(volumes, 50);
      const volSmaLine = chart.addSeries(LineSeries, {
        color: "#f59e0b",
        lineWidth: 1,
        priceScaleId: "volume",
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
      }, 1);
      volSmaLine.setData(
        volSma.map((v, i) => v !== null ? { time: rows[i].date as Time, value: v } : null)
          .filter(Boolean) as LineData<Time>[]
      );
    }

    const closes = rows.map((r) => r.close);
    const highs = rows.map((r) => r.high);
    const lows = rows.map((r) => r.low);
    const times = rows.map((r) => r.date as Time);

    const toLineData = (vals: (number | null)[]): LineData<Time>[] =>
      vals.map((v, i) => ({ time: times[i], value: v ?? undefined })).filter((d) => d.value !== undefined) as LineData<Time>[];

    if (ma1) {
      const ma = computeMA(closes, ma1);
      const s1 = chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 1, priceScaleId: "right", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false });
      s1.setData(toLineData(ma));
    }

    if (ma2) {
      const ma = computeMA(closes, ma2);
      const s2 = chart.addSeries(LineSeries, { color: "#8b5cf6", lineWidth: 1, priceScaleId: "right", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false });
      s2.setData(toLineData(ma));
    }

    if (overlay.startsWith("bb")) {
      const m = overlay.match(/^bb(\d+)(?:_(\d+))?$/);
      const period = m ? parseInt(m[1]) : 20;
      const mult = m && m[2] ? parseInt(m[2]) : 2;
      const bb = computeBollinger(closes, period, mult);
      const sUp = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1, priceScaleId: "right", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false });
      const sMid = chart.addSeries(LineSeries, { color: "#06b6d480", lineWidth: 1, priceScaleId: "right", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false });
      const sLo = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1, priceScaleId: "right", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false });
      sUp.setData(toLineData(bb.upper));
      sMid.setData(toLineData(bb.mid));
      sLo.setData(toLineData(bb.lower));
    }

    // Pane index for the oscillator is dynamic: pane 0 is the price pane,
    // pane 1 is volume (when shown), so the oscillator goes in pane 1 when
    // volume is omitted and pane 2 otherwise. Hard-coding pane=2 caused
    // lightweight-charts to fragment MACD across two panes for symbols
    // (like cash-settled indices) where the volume pane was skipped.
    const oscPane = hasVolume ? 2 : 1;
    if (oscillator.startsWith("rsi")) {
      const period = parseInt(oscillator.replace("rsi", ""));
      const rsi = computeRSI(closes, period);
      const sRsi = chart.addSeries(LineSeries, { color: "#a78bfa", lineWidth: 1, priceScaleId: "osc", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false }, oscPane);
      sRsi.setData(toLineData(rsi));
    } else if (oscillator.startsWith("macd")) {
      const [fast, slow, sig] = oscillator === "macd_fast" ? [8, 17, 9] : [12, 26, 9];
      const m = computeMACD(closes, fast, slow, sig);
      const sMacd = chart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1, priceScaleId: "osc", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false }, oscPane);
      const sSig = chart.addSeries(LineSeries, { color: "#f97316", lineWidth: 1, priceScaleId: "osc", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false }, oscPane);
      const sHist = chart.addSeries(HistogramSeries, { priceScaleId: "osc", lastValueVisible: false, priceLineVisible: false }, oscPane);
      sMacd.setData(toLineData(m.macd));
      sSig.setData(toLineData(m.signal));
      sHist.setData(m.histogram.map((v, i) => v !== null ? { time: times[i], value: v, color: v >= 0 ? "#22c55e80" : "#ef444480" } : null).filter(Boolean) as HistogramData<Time>[]);
    } else if (oscillator.startsWith("stoch")) {
      const kPeriod = oscillator === "stoch5" ? 5 : 14;
      const st = computeStochastic(highs, lows, closes, kPeriod, 3);
      const sK = chart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1, priceScaleId: "osc", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false }, oscPane);
      const sD = chart.addSeries(LineSeries, { color: "#f97316", lineWidth: 1, priceScaleId: "osc", lastValueVisible: false, crosshairMarkerVisible: false, priceLineVisible: false }, oscPane);
      sK.setData(toLineData(st.k));
      sD.setData(toLineData(st.d));
    }

    chartRef.current = chart;

    // Vertical lines at every Monday in the visible range, painted on an
    // overlay canvas. We hide the chart's built-in vertical gridlines (above)
    // so the only verticals are these. Re-drawn on every visible-range,
    // logical-range, and resize event.
    const drawMondayLines = () => {
      const cv = overlayRef.current;
      const cr = containerRef.current;
      if (!cv || !cr || !chartRef.current) return;
      const w = cr.clientWidth, h = cr.clientHeight;
      if (w <= 0 || h <= 0) return;
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = "#3a3a52";
      ctx.lineWidth = 1;
      const ts = chartRef.current.timeScale();
      const plotLeft = chartRef.current.priceScale("left").width();
      const plotRight = plotLeft + ts.width();
      const startDate = spreadOverlay?.entryTime?.slice(0, 10);
      const endDate = spreadOverlay?.exitTime?.slice(0, 10);
      const startTime = startDate
        ? candleData.find((d) => (d.time as string) >= startDate)?.time
        : candleData[0]?.time;
      const endTime = endDate
        ? candleData.find((d) => (d.time as string) >= endDate)?.time
        : candleData[candleData.length - 1]?.time;
      const startX = startTime === undefined ? null : ts.timeToCoordinate(startTime);
      const endX = endTime === undefined ? null : ts.timeToCoordinate(endTime);
      const shadeLeft = startX == null ? plotLeft : Math.max(plotLeft, Math.min(plotRight, plotLeft + startX));
      const shadeRight = endX == null ? plotRight : Math.max(plotLeft, Math.min(plotRight, plotLeft + endX));
      drawSpreadShading(ctx, candleSeries, shadeLeft, shadeRight, h);
      // Iterate every day that has a slot on the chart (real data + holiday
      // whitespace), so a Monday holiday still gets its weekly demarcation.
      const allDates = [...rows.map((r) => r.date), ...holidayWhitespace.map((w) => w.time as string)];
      // Collect Mondays with their on-screen positions, sorted left-to-right.
      type Mon = { ds: string; xc: number; y: number; m: number; d: number };
      const mondays: Mon[] = [];
      for (const ds of allDates) {
        const parts = ds.split("-").map(Number);
        if (parts.length !== 3) continue;
        // UTC Date construction sidesteps host-timezone drift on the day-of-week.
        const dow = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay();
        if (dow !== 1) continue; // 0=Sun, 1=Mon
        const xc = ts.timeToCoordinate(ds as Time);
        if (xc == null) continue;
        const x = plotLeft + xc;
        if (x < 0 || x > w) continue;
        mondays.push({ ds, xc: x, y: parts[0], m: parts[1], d: parts[2] });
      }
      mondays.sort((a, b) => a.xc - b.xc);
      // Vertical Monday lines (every Monday — weekly demarcation).
      for (const m of mondays) {
        const px = Math.round(m.xc) + 0.5;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, h);
        ctx.stroke();
      }
      // Labels: every other Monday gets a day-of-month tag; any Monday that
      // is the FIRST Monday of its month always gets a full m/d/yy label,
      // regardless of the bi-weekly cadence — that's the calendar anchor on
      // the axis. A Monday is the first of its month iff its day-of-month
      // is 1..7.
      ctx.fillStyle = "#a0a0b0";
      ctx.font = "10px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      const labelY = h - 4;
      for (let i = 0; i < mondays.length; i++) {
        const m = mondays[i];
        const firstOfMonth = m.d >= 1 && m.d <= 7;
        if (!firstOfMonth && i % 2 !== 0) continue;
        const label = firstOfMonth
          ? `${m.m}/${m.d}/${String(m.y).slice(2)}`
          : String(m.d);
        ctx.fillText(label, m.xc, labelY);
      }
      // Expiration marker: a dashed vertical line plus a date label at the
      // expiration column, in the strike color so it reads as part of the
      // spread overlay. Only drawn when an expiration is supplied.
      const expDs = spreadOverlay?.expirationDate;
      if (expDs) {
        const expTime = candleData.find((d) => (d.time as string) >= expDs)?.time
          ?? candleData[candleData.length - 1]?.time;
        const exc = expTime === undefined ? null : ts.timeToCoordinate(expTime);
        if (exc != null) {
          const ex = plotLeft + exc;
          ctx.save();
          ctx.strokeStyle = "#facc15";
          ctx.setLineDash([3, 3]);
          const px = Math.round(ex) + 0.5;
          ctx.beginPath();
          ctx.moveTo(px, 0);
          ctx.lineTo(px, h);
          ctx.stroke();
          ctx.restore();
          const [ey, em, ed] = expDs.split("-").map(Number);
          ctx.fillStyle = "#facc15";
          ctx.fillText(`${em}/${ed}/${String(ey).slice(2)}`, ex, labelY);
        }
      }
      // Focus marker: solid cyan vertical line at the crossing date, labeled at
      // the top, so the ±3-month context around it is obvious at a glance.
      if (focusDate) {
        const fTime = candleData.find((d) => (d.time as string) >= focusDate)?.time;
        const fxc = fTime === undefined ? null : ts.timeToCoordinate(fTime);
        if (fxc != null) {
          const fx = plotLeft + fxc;
          ctx.save();
          ctx.strokeStyle = "#06b6d4";
          ctx.setLineDash([4, 3]);
          ctx.lineWidth = 1.5;
          const px = Math.round(fx) + 0.5;
          ctx.beginPath();
          ctx.moveTo(px, 0);
          ctx.lineTo(px, h);
          ctx.stroke();
          ctx.fillStyle = "#06b6d4";
          ctx.font = "10px monospace";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          const [fy, fm, fd] = focusDate.split("-").map(Number);
          ctx.fillText(`crossed ${fm}/${fd}/${String(fy).slice(2)}`, fx, 4);
          ctx.restore();
        }
      }
    };
    // Subscribe FIRST so the initial visible-range set (below) fires a
    // draw — drawing before the time scale is laid out makes
    // timeToCoordinate return null for every row.
    chart.timeScale().subscribeVisibleTimeRangeChange(drawMondayLines);
    chart.timeScale().subscribeVisibleLogicalRangeChange(drawMondayLines);

    // Visible range defaults to the last 6 months; the prior 12 months of
    // lookback live behind it (used for indicator computation) but stay
    // off-screen until the user pans. Setting this — instead of fitContent
    // — keeps the visible chart unchanged while every indicator gets
    // its full required lookback.
    if (rows.length > 0) {
      const totalSlots = candleData.length;
      if (focusDate) {
        // Focus mode: visible range is the 3 months before and after the date.
        const f = new Date(focusDate + "T00:00:00Z");
        const ds = new Date(f); ds.setUTCMonth(ds.getUTCMonth() - 3);
        const de = new Date(f); de.setUTCMonth(de.getUTCMonth() + 3);
        const displayStart = ds.toISOString().slice(0, 10);
        const displayEnd = de.toISOString().slice(0, 10);
        let fromSlot = 0;
        for (let s = 0; s < totalSlots; s++) {
          if ((candleData[s].time as string) >= displayStart) { fromSlot = s; break; }
        }
        let toSlot = totalSlots - 1;
        for (let s = totalSlots - 1; s >= 0; s--) {
          if ((candleData[s].time as string) <= displayEnd) { toSlot = s; break; }
        }
        chart.timeScale().setVisibleLogicalRange({ from: fromSlot, to: toSlot });
      } else {
        const displayStartDate = new Date();
        displayStartDate.setMonth(displayStartDate.getMonth() - 6);
        const displayStart = displayStartDate.toISOString().slice(0, 10);
        let fromSlot = 0;
        for (let s = 0; s < totalSlots; s++) {
          if ((candleData[s].time as string) >= displayStart) { fromSlot = s; break; }
        }
        // When a spread overlay is shown, the expiration column lands at the far
        // right; reserve a few empty slots past it so the expiration date label
        // has room instead of being clipped against the price scale.
        const rightPad = spreadOverlay?.expirationDate ? 3 : 0;
        chart.timeScale().setVisibleLogicalRange({ from: fromSlot, to: totalSlots - 1 + rightPad });
      }
    }
    // Belt-and-braces: a deferred draw next frame catches the case where
    // fitContent doesn't actually fire a range change (e.g., rows were
    // already in the autofit range from a prior render).
    requestAnimationFrame(drawMondayLines);

    // OHLC(+V) readout above the chart: track the crosshaired bar, falling back
    // to the latest bar when the cursor leaves. Volume is looked up by date
    // (the candle data point carries no volume); null for volume-less indices.
    const volByDate = new Map(rows.map((r) => [r.date, r.volume]));
    // Change = close − previous bar's close, keyed by date.
    const prevCloseByDate = new Map<string, number>();
    for (let i = 1; i < rows.length; i++) prevCloseByDate.set(rows[i].date, rows[i - 1].close);
    const chgOf = (date: string, close: number) => {
      const pc = prevCloseByDate.get(date);
      return pc === undefined ? null : close - pc;
    };
    const lastRow = rows.length ? rows[rows.length - 1] : null;
    // "2026-06-05" → "Fri 6/5/26" (UTC parse avoids host-tz day drift).
    const fmtDate = (ds: string) => {
      const [y, m, d] = ds.split("-").map(Number);
      const wd = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short" });
      return `${wd} ${m}/${d}/${String(y).slice(2)}`;
    };
    const showRow = (r: PriceRow | null) =>
      setOhlc(r ? { d: fmtDate(r.date), o: r.open, h: r.high, l: r.low, c: r.close, chg: chgOf(r.date, r.close), v: hasVolume ? r.volume : null } : null);
    showRow(lastRow);
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.time === undefined) { showRow(lastRow); return; }
      const d = param.seriesData.get(candleSeries) as
        | { open: number; high: number; low: number; close: number }
        | undefined;
      if (d && d.open !== undefined) {
        const date = param.time as string;
        setOhlc({ d: fmtDate(date), o: d.open, h: d.high, l: d.low, c: d.close, chg: chgOf(date, d.close), v: hasVolume ? (volByDate.get(date) ?? 0) : null });
      }
    });

    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        const rw = containerRef.current.clientWidth;
        const rh = containerRef.current.clientHeight;
        if (rw > 0 && rh > 0) {
          chart.applyOptions({ width: rw, height: rh });
          drawMondayLines();
        }
      }
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.timeScale().unsubscribeVisibleTimeRangeChange(drawMondayLines);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(drawMondayLines);
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [rows, ma1, ma2, overlay, oscillator, timeframe, spreadOverlay, drawSpreadShading, focusDate, focusLevel]);

  // Live polling: while the market is open, fetch the current-day quote every
  // few seconds and push it into the last bar via series.update() (which
  // replaces the bar when the time matches, or appends a new day). This avoids
  // rebuilding the chart so zoom/pan stays put.
  useEffect(() => {
    // No live polling in focus mode — it's a fixed historical window.
    if (timeframe !== "1D" || !marketOpen || !symbol || focusDate) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const quote = await apiGet<PriceRow>(`/prices/${encodeURIComponent(symbol)}/quote`);
        if (cancelled || !quote || quote.open <= 0) return;
        candleSeriesRef.current?.update({
          time: quote.date as Time,
          open: quote.open, high: quote.high, low: quote.low, close: quote.close,
        });
        volumeSeriesRef.current?.update({
          time: quote.date as Time,
          value: quote.volume,
          color: quote.close >= quote.open ? "#22c55e40" : "#ef444440",
        });
      } catch { /* transient quote failure — try again next tick */ }
    };
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [marketOpen, symbol, timeframe, focusDate]);

  // Intraday (5m) mode: self-contained chart + live poll. Builds a simple
  // candlestick+volume chart (no MA/Monday-line machinery — those are daily
  // concepts) using epoch-second timestamps, fetches today's 5-minute bars
  // from /intraday, and re-polls every 5s for live updates.
  useEffect(() => {
    if (timeframe !== "5m" || !symbol || !containerRef.current) return;
    // Clear any leftover Monday-line overlay painted by the daily chart.
    const ov = overlayRef.current;
    if (ov) { const c = ov.getContext("2d"); c?.clearRect(0, 0, ov.width, ov.height); }

    setLoading(true);
    const el = containerRef.current;
    // Lightweight-charts renders timestamps in UTC; format axis + crosshair in
    // US/Eastern (market time) instead. Intl uses the tz database so DST is
    // handled automatically.
    const etTime = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true,
    });
    const chart = createChart(el, {
      width: el.clientWidth || 600,
      height: el.clientHeight || 300,
      layout: { background: { color: "transparent" }, textColor: "#a0a0b0" },
      // The 5m frame paints fixed ET gridlines on the overlay canvas below.
      // Disable the library's auto verticals so the chart has one x-grid.
      grid: { vertLines: { visible: false }, horzLines: { color: "#2a2a3e" } },
      // Hide the crosshair time label on the axis; date is on the OHLC line.
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      timeScale: {
        borderColor: "#2a2a3e", timeVisible: true, secondsVisible: false,
        // Blank the library's auto tick labels — they land on ugly times (9:33,
        // 11:47…). We paint our own fixed ET ticks on the overlay canvas below.
        tickMarkFormatter: () => "",
      },
      // Crosshair tooltip still shows the precise bar time in ET.
      localization: {
        timeFormatter: (t: number) => etTime.format(new Date(t * 1000)),
      },
      rightPriceScale: { borderColor: "#2a2a3e", mode: 1 /* Logarithmic */ },
    });
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e", downColor: "#ef4444",
      borderUpColor: "#22c55e", borderDownColor: "#ef4444",
      wickUpColor: "#22c55e", wickDownColor: "#ef4444",
      priceLineVisible: false,
    });
    const previousCloseSeries = chart.addSeries(LineSeries, {
      color: "rgba(250, 204, 21, 0.55)",
      lineWidth: 2,
      priceScaleId: "right",
      lastValueVisible: true,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      title: "Prev close",
    });
    const shortStrikeSeries = spreadOverlay ? chart.addSeries(LineSeries, {
      color: "rgba(250, 204, 21, 0.55)",
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      priceScaleId: "right",
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      title: "",
    }) : null;
    const longStrikeSeries = spreadOverlay ? chart.addSeries(LineSeries, {
      color: "#facc15",
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      priceScaleId: "right",
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      title: "",
    }) : null;
    // Volume is added lazily — only when the session actually reports volume.
    // If every bar has zero/no volume, we skip the histogram so the candles get
    // the full height instead of an empty volume strip.
    let volume: ISeriesApi<"Histogram"> | null = null;
    let previousClosePrice: number | null = null;
    let previousCloseSessionKey: string | null = null;
    chartRef.current = chart;

    // OHLC(+V) readout: track the crosshaired bar, fall back to the latest bar.
    const vol5 = new Map<number, number>();
    const prevClose5 = new Map<number, number>();
    let hasVol5 = false;
    let last5: { d: string; o: number; h: number; l: number; c: number; chg: number | null; v: number | null } | null = null;
    // Bar timestamp → "Wed 4/8 3:45 PM" in ET (intraday axis shows only times).
    const etDT = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", weekday: "short", month: "numeric", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
    const fmt5 = (ms: number) => {
      const p = etDT.formatToParts(new Date(ms));
      const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
      return `${g("weekday")} ${g("month")}/${g("day")} ${g("hour")}:${g("minute")} ${g("dayPeriod")}`;
    };
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.time === undefined) { setOhlc(last5); return; }
      const d = param.seriesData.get(candle) as
        | { open: number; high: number; low: number; close: number }
        | undefined;
      if (d && d.open !== undefined) {
        const t = param.time as number;
        const pc = prevClose5.get(t);
        setOhlc({ d: fmt5(t * 1000), o: d.open, h: d.high, l: d.low, c: d.close, chg: pc === undefined ? null : d.close - pc, v: hasVol5 ? (vol5.get(t) ?? 0) : null });
      }
    });

    // Fixed, readable x-axis ticks at round market times instead of whatever
    // the library auto-picks. 4:00 has no bar (last bar opens 3:55) so it's
    // anchored to the final bar. ET hour/minute via Intl so DST is automatic.
    const etHM = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const etYMDHMS = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const TICKS = [
      { h: 9, m: 30, label: "9:30" },
      { h: 10, m: 0, label: "10" },
      { h: 11, m: 0, label: "11" },
      { h: 12, m: 0, label: "12" },
      { h: 13, m: 0, label: "1" },
      { h: 14, m: 0, label: "2" },
      { h: 15, m: 0, label: "3" },
      { h: 16, m: 0, label: "4" },
      { h: 16, m: 30, label: "4:30" },
    ];
    let dayTimes: number[] = []; // epoch seconds of every slot (whitespace + bars)
    // The 5m frame is ALWAYS the regular session, no exceptions, even with zero
    // bars. Each gridline runs through the CENTER of its candle: the 09:30
    // candle is centered on the plot's left edge and 16:30 on its right edge.
    const FRAME_START_MIN = 570; // 09:30 ET — first slot, centered on left edge
    const FRAME_END_MIN = 990; // 16:30 ET (4:30pm) — last slot, centered on right edge
    const etPartsOf = (d: Date) =>
      etYMDHMS.formatToParts(d).reduce<Record<string, string>>(
        (a, x) => ((a[x.type] = x.value), a), {});
    const etDateKey = (t: number) => {
      const p = etPartsOf(new Date(t * 1000));
      return `${p.year}-${p.month}-${p.day}`;
    };
    const startForPreviousClose = (sessionKey: string) => {
      const [y, m, d] = sessionKey.split("-").map(Number);
      const start = new Date(Date.UTC(y, m - 1, d));
      start.setUTCDate(start.getUTCDate() - 21);
      return start.toISOString().slice(0, 10);
    };
    const syncPreviousCloseSeries = () => {
      if (previousClosePrice == null || dayTimes.length < 2) {
        previousCloseSeries.setData([]);
        return;
      }
      previousCloseSeries.setData([
        { time: dayTimes[0] as Time, value: previousClosePrice },
        { time: dayTimes[dayTimes.length - 1] as Time, value: previousClosePrice },
      ]);
    };
    const syncStrikeSeries = () => {
      if (!spreadOverlay || !shortStrikeSeries || !longStrikeSeries || dayTimes.length < 2) return;
      const entryMs = spreadOverlay.entryTime ? Date.parse(spreadOverlay.entryTime) : NaN;
      const exitMs = spreadOverlay.exitTime ? Date.parse(spreadOverlay.exitTime) : NaN;
      const startIndex = Number.isFinite(entryMs)
        ? dayTimes.findIndex((t) => t * 1000 >= entryMs)
        : 0;
      const foundEndIndex = Number.isFinite(exitMs)
        ? dayTimes.findIndex((t) => t * 1000 >= exitMs)
        : -1;
      const endIndex = foundEndIndex >= 0 ? foundEndIndex : dayTimes.length - 1;
      const strikeLineData = (strike: number) =>
        startIndex < 0 || startIndex > endIndex
          ? []
          : dayTimes.map((t, i) => (
              i >= startIndex && i <= endIndex
                ? { time: t as Time, value: strike }
                : { time: t as Time }
            ));
      shortStrikeSeries.setData(strikeLineData(spreadOverlay.shortStrike));
      longStrikeSeries.setData(strikeLineData(spreadOverlay.longStrike));
    };
    const updatePreviousCloseLine = async (sessionKey: string) => {
      if (previousCloseSessionKey === sessionKey) return;
      previousCloseSessionKey = sessionKey;
      const resp = await apiGet<{ rows: PriceRow[] }>(
        `/prices/${encodeURIComponent(symbol)}?start=${startForPreviousClose(sessionKey)}&limit=100`
      );
      if (cancelled) return;
      const previous = (resp.rows ?? [])
        .filter((r) => r.date < sessionKey && r.close > 0)
        .sort((a, b) => a.date.localeCompare(b.date))
        .at(-1);
      if (!previous) {
        previousClosePrice = null;
        syncPreviousCloseSeries();
        return;
      }
      previousClosePrice = previous.close;
      syncPreviousCloseSeries();
    };
    // Epoch-second helpers anchored to the active ET session date.
    const etDayBase = (sessionTime?: number) => {
      const anchor = sessionTime === undefined ? new Date() : new Date(sessionTime * 1000);
      const p = etPartsOf(anchor);
      // ET = UTC + offMs (handles EST/EDT since it's derived from wall time).
      const offMs = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - anchor.getTime();
      return { Y: +p.year, Mo: +p.month, D: +p.day, offMs };
    };
    const slotEpoch = (b: { Y: number; Mo: number; D: number; offMs: number }, min: number) =>
      Math.round((Date.UTC(b.Y, b.Mo - 1, b.D, Math.floor(min / 60), min % 60) - b.offMs) / 1000);
    const etMinOf = (t: number) => {
      const p = etHM.formatToParts(new Date(t * 1000));
      const h = +(p.find((x) => x.type === "hour")?.value ?? "0");
      const m = +(p.find((x) => x.type === "minute")?.value ?? "0");
      return h * 60 + m;
    };
    const drawTicks = () => {
      const cv = overlayRef.current, cr = containerRef.current, ch = chartRef.current;
      if (!cv || !cr || !ch) return;
      const w = cr.clientWidth, h = cr.clientHeight;
      if (w <= 0 || h <= 0) return;
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!dayTimes.length) return;
      const ts = ch.timeScale();
      const plotLeft = ch.priceScale("left").width();
      const plotRight = plotLeft + ts.width();
      const entryMs = spreadOverlay?.entryTime ? Date.parse(spreadOverlay.entryTime) : NaN;
      const startTime = spreadOverlay && Number.isFinite(entryMs)
        ? dayTimes.find((t) => t * 1000 >= entryMs)
        : dayTimes[0];
      const exitMs = spreadOverlay?.exitTime ? Date.parse(spreadOverlay.exitTime) : NaN;
      const endTime = spreadOverlay && Number.isFinite(exitMs)
        ? dayTimes.find((t) => t * 1000 >= exitMs)
        : dayTimes[dayTimes.length - 1];
      const startX = startTime === undefined ? null : ts.timeToCoordinate(startTime as Time);
      const endX = endTime === undefined ? null : ts.timeToCoordinate(endTime as Time);
      const shadeLeft = startX == null ? plotLeft : Math.max(plotLeft, Math.min(plotRight, plotLeft + startX));
      const shadeRight = endX == null ? plotRight : Math.max(plotLeft, Math.min(plotRight, plotLeft + endX));
      drawSpreadShading(ctx, candle, shadeLeft, shadeRight, h);
      // timeToCoordinate is plot-area relative; the overlay spans the whole
      // chart container, so offset x into the same space before painting.
      const byMin = new Map<number, number>();
      for (const t of dayTimes) {
        const cx = ts.timeToCoordinate(t as Time);
        if (cx != null) {
          const x = plotLeft + cx;
          byMin.set(etMinOf(t), x);
        }
      }
      ctx.strokeStyle = "#6b6b8a";
      ctx.lineWidth = 1.5;
      ctx.fillStyle = "#c8c8d8";
      ctx.font = "10px monospace";
      ctx.textBaseline = "bottom";
      const labelY = h - 4;
      for (const tk of TICKS) {
        const x = byMin.get(tk.h * 60 + tk.m);
        if (x == null) continue;
        const px = Math.round(x) + 0.5;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, h);
        ctx.stroke();
        // Keep edge labels from clipping.
        ctx.textAlign = x < plotLeft + 12 ? "left" : x > plotRight - 12 ? "right" : "center";
        ctx.fillText(tk.label, x, labelY);
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(drawTicks);

    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth, h = containerRef.current.clientHeight;
      if (w > 0 && h > 0) { chart.applyOptions({ width: w, height: h }); drawTicks(); }
    });
    ro.observe(el);

    let cancelled = false;
    let firstLoad = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const acct = intradayAccountHash(accountHash);
        const q = acct ? `?account=${encodeURIComponent(acct)}` : "";
        const r = await apiGet<{ candles: { time: number; open: number; high: number; low: number; close: number; volume: number }[] }>(
          `/prices/${encodeURIComponent(symbol)}/intraday${q}`
        );
        if (cancelled) return;
        // Keep only bars inside the session (09:30 → 16:30 slot). Real bars sit
        // on the 5-min grid, so they coincide with the whitespace slots.
        const inFrame = (r.candles ?? []).filter((c) => {
          const m = etMinOf(c.time);
          return m >= FRAME_START_MIN && m <= FRAME_END_MIN;
        });
        const sessionKey = inFrame.length ? etDateKey(inFrame[inFrame.length - 1].time) : etDateKey(Math.floor(Date.now() / 1000));
        const candles = inFrame.filter((c) => etDateKey(c.time) === sessionKey);
        await updatePreviousCloseLine(sessionKey).catch(() => {
          previousCloseSessionKey = null;
        });
        const real = new Map(candles.map((c) => [c.time, c]));
        // Lay the full 09:30→16:30 grid as whitespace so the axis ALWAYS spans
        // the whole session even with zero bars; real bars overlay their slot.
        const base = etDayBase(candles[candles.length - 1]?.time);
        const slots: number[] = [];
        for (let m = FRAME_START_MIN; m <= FRAME_END_MIN; m += 5) slots.push(slotEpoch(base, m));
        const allTimes = Array.from(new Set([...slots, ...candles.map((c) => c.time)]))
          .sort((a, b) => a - b);
        dayTimes = allTimes;
        syncPreviousCloseSeries();
        syncStrikeSeries();
        candle.setData(allTimes.map((t) => {
          const c = real.get(t);
          return c
            ? { time: t as Time, open: c.open, high: c.high, low: c.low, close: c.close }
            : { time: t as Time }; // whitespace — holds the axis open
        }));
        const hasVolume = candles.some((c) => c.volume > 0);
        if (hasVolume) {
          if (!volume) {
            // Dedicated pane, same margins as the daily chart — not the old
            // overlay trick (top: 0.8) which squashed bars into a thin line.
            volume = chart.addSeries(HistogramSeries, {
              color: "#4a90d9",
              priceFormat: { type: "volume" as const },
              priceScaleId: "volume",
            }, 1);
            volume.priceScale().applyOptions({
              scaleMargins: { top: 0.1, bottom: 0 },
            });
          }
          volume.setData(candles.map((c) => ({
            time: c.time as Time, value: c.volume,
            color: c.close >= c.open ? "#22c55e40" : "#ef444440",
          })));
        } else if (volume) {
          chart.removeSeries(volume);
          volume = null;
        }
        // Feed the OHLC(+V) readout: volume-by-time lookup and the latest bar.
        hasVol5 = hasVolume;
        vol5.clear();
        prevClose5.clear();
        candles.forEach((c, i) => {
          vol5.set(c.time, c.volume);
          if (i > 0) prevClose5.set(c.time, candles[i - 1].close);
        });
        const lc = candles[candles.length - 1];
        const lcPc = candles.length > 1 ? candles[candles.length - 2].close : undefined;
        last5 = lc ? { d: fmt5(lc.time * 1000), o: lc.open, h: lc.high, l: lc.low, c: lc.close, chg: lcPc === undefined ? null : lc.close - lcPc, v: hasVolume ? lc.volume : null } : null;
        if (firstLoad) { setOhlc(last5); firstLoad = false; }
        // Pin the frame by LOGICAL range every load. The ±0.5 half-bar margins
        // mean index 0 (09:30) and index N−1 (16:30) are each FULLY visible with
        // their gridline running through the candle's center — the 09:30 candle
        // sits centered on the 09:30 gridline, 16:30 on the 4:30 gridline.
        chart.timeScale().setVisibleLogicalRange({ from: -0.5, to: allTimes.length - 0.5 });
        requestAnimationFrame(drawTicks);
        setLoading(false);
      } catch { if (!cancelled) setLoading(false); }
      finally { inFlight = false; }
    };
    load();
    const id = setInterval(load, 5000);

    return () => {
      cancelled = true;
      clearInterval(id);
      ro.disconnect();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(drawTicks);
      chart.remove();
      chartRef.current = null;
      const c = overlayRef.current?.getContext("2d");
      if (c && overlayRef.current) c.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    };
  }, [symbol, timeframe, spreadOverlay, drawSpreadShading, accountHash]);

  return (
    <div className={`flex flex-col ${className ?? ""}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-cyan">{symbol}</span>
          {/* Timeframe toggle: 1D daily history vs 5m intraday (today). Hidden in
              focus mode — intraday is today-only, irrelevant to a historical window. */}
          <div className={`flex items-center rounded border border-border overflow-hidden text-xs font-medium ${focusDate ? "hidden" : ""}`}>
            {(["1D", "5m"] as const).map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2 py-0.5 transition-colors ${
                  timeframe === tf ? "bg-cyan/30 text-cyan" : "text-muted hover:text-foreground"
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
          {timeframe === "1D" && [
            { label: "MA1", value: ma1, set: setMA1, opts: MA_OPTIONS, color: "text-amber" },
            { label: "MA2", value: ma2, set: setMA2, opts: MA_OPTIONS, color: "text-purple" },
            { label: "Overlay", value: overlay, set: setOverlay, opts: OVERLAY_OPTIONS, color: "text-cyan" },
            { label: "Oscillator", value: oscillator, set: setOscillator, opts: OSCILLATOR_OPTIONS, color: "text-blue-400" },
          ].map((d) => (
            <CustomSelect
              key={d.label}
              value={d.value}
              onChange={d.set}
              options={[
                { value: "", label: d.label },
                ...d.opts.map((o) => ({ value: o.value, label: o.label })),
              ]}
              className={`!px-1 !py-0.5 ${d.value ? `!${d.color}` : ""}`}
            />
          ))}
          {/* OHLC(+V) of the crosshaired bar (latest when not hovering). Sits in
              the header so it costs no vertical space from the chart. */}
          {ohlc && (() => {
            const col = ohlc.c >= ohlc.o ? "text-green" : "text-red";
            const px = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (
              <div className="flex items-center gap-2 text-xs font-mono tabular-nums">
                <span className="text-foreground">{ohlc.d}</span>
                <span className="text-muted">O <span className={col}>{px(ohlc.o)}</span></span>
                <span className="text-muted">H <span className={col}>{px(ohlc.h)}</span></span>
                <span className="text-muted">L <span className={col}>{px(ohlc.l)}</span></span>
                <span className="text-muted">
                  C <span className={col}>{px(ohlc.c)}</span>
                  {ohlc.chg != null && (
                    <span className={ohlc.chg >= 0 ? "text-green" : "text-red"}> {ohlc.chg >= 0 ? "+" : ""}{px(ohlc.chg)}</span>
                  )}
                </span>
                {ohlc.v != null && (
                  <span className="text-muted">V <span className="text-foreground">{Math.round(ohlc.v).toLocaleString()}</span></span>
                )}
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`https://www.nasdaq.com/market-activity/stocks/${encodeURIComponent(symbol.replace(/^\$/, "").toLowerCase())}/news-headlines`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium px-2 py-0.5 rounded bg-cyan/20 text-cyan hover:bg-cyan/30 transition-colors"
            title={`Open ${symbol} news headlines at nasdaq.com (new tab)`}
          >
            News
          </a>
          <button onClick={onClose} className="text-muted hover:text-foreground" title="Close">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Wrapper is the positioning context for the Monday-lines overlay; the
          chart and the overlay canvas are siblings inside it. The container is
          ALWAYS mounted (both the daily and intraday build effects attach the
          chart to it) — the spinner is overlaid on top while loading. */}
      <div className="flex-1 relative w-full rounded-lg border border-border bg-surface overflow-hidden" style={{ minHeight: "300px" }}>
        <canvas ref={overlayRef} className="absolute inset-0 pointer-events-none rounded-lg" style={{ zIndex: 0 }} />
        <div ref={containerRef} className="absolute inset-0 rounded-lg" style={{ zIndex: 1 }} />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 20 }}>
            <Spinner className={`${s.statusIcon} text-muted`} />
          </div>
        )}
      </div>
    </div>
  );
}
